import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {productGetAll, productExcel} from '../../../redux/slices/product.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

import {pagination} from '../../../helpers'

const selectProductData = (state) => ({
  product: state.product.product,
  loading: state.product.loading,
})

function List() {
  const intl = useIntl()
  const {product, loading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    if (params) dispatch(productGetAll(params))
  }, [params, dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        productExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.PRODUCT_SERVICES'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PRODUCT.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PRODUCT.SEARCH.BASEPURCHASEPRICE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'basePurchasePrice',
        Cell: ({...props}) => <CustomCell item={props} price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PRODUCT.SEARCH.BASESALEPRICE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'baseSalePrice',
        Cell: ({...props}) => <CustomCell item={props} price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'PRODUCT.SEARCH.STOCKCOUNT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'stockCount',
        Cell: ({...props}) => <CustomCellDouble item={props} double='productUnitName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
    ],
    [handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PRODUCT_SERVICES'})}</PageTitle>
      <KTCard>
        <ListHeader search={handleSearch} actionButtons={actionButtons} />
        <KTCardBody>
          {!product && <ListLoading />}
          {product && (
            <Table items={product} cols={Columns} handlePagination={handlePagination} paddless />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
